import { ModalBody, ModalFooter, Spinner } from 'reactstrap'
import CustomModal from 'views/pages/CustomModal'
import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import AxiosInstance from 'utils/AxiosInstance'
import {
  getEmbedUrl,
  getFileExtension,
  getFileKind,
  getPdfToolbarSuffix,
} from '../fileTypes'
import { getIconFileType } from 'utils/helpers'

// Cap what we render for plain-text previews so a huge csv/log can't freeze the tab.
const MAX_TEXT_CHARS = 500000

// The modal is nearly full screen (see the fullHeight prop of CustomModal); every viewer fills it.
const viewerStyle = {
  width: '100%',
  height: '100%',
}

const frameStyle = { width: '100%', height: '100%', border: 0 }

const centeredStyle = {
  ...viewerStyle,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  textAlign: 'center',
}

function UrlModal(props) {
  const language = props.language || {}
  const { pdfjsDownloadAndPrint } = useSelector((store) => store.generalConfig)

  const libraryFile = props.library.find(
    (file) => file.id === +props.match.params.id,
  )

  const [file, setFile] = useState(libraryFile)
  // Set when the browser can't render the file inline (bad codec, blocked fetch, ...)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [textContent, setTextContent] = useState(null)

  useEffect(() => {
    if (!libraryFile) {
      AxiosInstance.get(`/library/single-file/${props.match.params.id}`).then(
        (data) => {
          setFile(data)
        },
      )
    } else {
      setFile(libraryFile)
    }
  }, [libraryFile, props.match.params.id])

  const path = file?.path
  const kind = previewFailed ? 'download' : getFileKind(path)

  useEffect(() => {
    setPreviewFailed(false)
    setTextContent(null)
  }, [path])

  useEffect(() => {
    if (kind !== 'text' || !path) return

    let cancelled = false
    fetch(path)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load file')
        return response.text()
      })
      .then((text) => {
        if (cancelled) return
        setTextContent(
          text.length > MAX_TEXT_CHARS
            ? `${text.slice(0, MAX_TEXT_CHARS)}\n\n… (preview truncated)`
            : text,
        )
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [kind, path])

  const backPath = props.parentId
    ? '/library/folder/' + props.parentId
    : '/library'

  const openHref = path
    ? `${path}${getPdfToolbarSuffix(file, pdfjsDownloadAndPrint)}`
    : ''

  const renderViewer = () => {
    if (!path) {
      return (
        <div style={centeredStyle}>
          <Spinner color='info' />
        </div>
      )
    }

    switch (kind) {
      case 'pdf':
        return <iframe title={file.name} src={openHref} style={frameStyle} />

      case 'office':
        // Office Online renders doc/xls/ppt files straight from their public URL.
        return (
          <iframe
            title={file.name}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              path,
            )}`}
            style={frameStyle}
          />
        )

      case 'image':
        return (
          <div style={{ ...centeredStyle, padding: '16px' }}>
            <img
              src={path}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
              onError={() => setPreviewFailed(true)}
            />
          </div>
        )

      case 'video':
        return (
          <div style={{ ...centeredStyle, background: '#000' }}>
            <video
              src={path}
              controls
              playsInline
              style={{ maxWidth: '100%', maxHeight: '100%' }}
              onError={() => setPreviewFailed(true)}
            />
          </div>
        )

      case 'audio':
        return (
          <div style={centeredStyle}>
            <audio
              src={path}
              controls
              style={{ width: '80%' }}
              onError={() => setPreviewFailed(true)}
            />
          </div>
        )

      case 'text':
        return textContent === null ? (
          <div style={centeredStyle}>
            <Spinner color='info' />
          </div>
        ) : (
          <pre
            style={{
              ...viewerStyle,
              margin: 0,
              padding: '16px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {textContent}
          </pre>
        )

      case 'html':
        // Uploaded html runs isolated from the app (no same-origin access).
        return (
          <iframe
            title={file.name}
            src={path}
            style={frameStyle}
            sandbox='allow-scripts allow-popups allow-forms'
          />
        )

      case 'download':
        return (
          <div style={centeredStyle}>
            <FontAwesomeIcon
              className='text-info mb-3'
              icon={getIconFileType(getFileExtension(path))}
              size='5x'
            />
            <h4 className='mb-1'>{file.name}</h4>
            <p className='text-muted mb-3'>
              This file can't be previewed in the browser.
            </p>
            <a
              href={path}
              target='_blank'
              rel='noreferrer'
              download
              className='btn btn-info'
            >
              <FontAwesomeIcon icon={faDownload} className='mr-2' />
              Download
            </a>
          </div>
        )

      // 'frame' (txt/json/xml, rendered natively) and 'web' (any pasted URL)
      default:
        return (
          <iframe
            title={file.name}
            src={kind === 'web' ? getEmbedUrl(path) : path}
            style={frameStyle}
            allow='autoplay; fullscreen; picture-in-picture'
            allowFullScreen
          />
        )
    }
  }

  return (
    <CustomModal backPath={backPath} title={file?.name} size='xl' fullHeight>
      <ModalBody
        className='p-0 overflow-hidden'
        style={{ position: 'relative' }}
      >
        <div
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        >
          {renderViewer()}
        </div>
      </ModalBody>

      <ModalFooter className='py-3 border-top'>
        {openHref && (
          <a
            href={openHref}
            target='_blank'
            rel='noreferrer'
            className='btn btn-outline-info py-2'
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} className='mr-2' />
            Open in new tab
          </a>
        )}
        <Link to={backPath} className='btn py-2 btn-transparent active-link'>
          {language.general ? language.general.cancel : null}
        </Link>
      </ModalFooter>
    </CustomModal>
  )
}

export default UrlModal
